import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { Grid } from 'react-bootstrap';
import { get } from 'lodash';
import moment from 'moment';
import memoize from 'memoize-one';
import fastMemoize from 'fast-memoize';
import Select from 'react-select';
import ButtonStandardPrimary from 'components/atoms/buttons/standard/Primary';
import { LoadingTile } from 'components/molecules/ErrorTile';
import { fetchList } from 'services/dynamicProfiles/duck';
import DebounceInputChange from 'hoc/DebounceInputChange';
import StyledReactTable from 'components/molecules/StyledReactTable';
import Pagination from 'components/molecules/tables/components/Pagination';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ActiveStatusLabel from 'components/atoms/labels/ActiveStatusLabel';
import { INNOVID_DYNAMIC_PROFILE } from 'constants/capabilities';

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 25;

const DebouncedInput = DebounceInputChange(200)(props => <input {...props} />);

const generateFilterValue = (filter, row) => row[filter.id];
const memoizeFilterValue = fastMemoize(generateFilterValue);

const generateFilterArrayValue = (filter, row) => row[filter.id].join(', ');
const memoizeFilterArrayValue = fastMemoize(generateFilterArrayValue);

class DynamicProfilesTableContainer extends Component {
    constructor(props) {
        super(props);
        this.getColumns = this.getColumns.bind(this);
        this.activeSearch = false;
        this.state = {
            showPagination: false,
            currentUrl: props.parsedUrl.href,
            list: null,
            optionsByBrand: {},
            ...DynamicProfilesTableContainer.convertUriToState(props.parsedUrl)
        };

        this.createBrandOptions = memoize(DynamicProfilesTableContainer.createBrandOptions);
        this.dynamicProfileTableInstance = React.createRef();
        this.setActiveSearch = this.setActiveSearch.bind(this);
    }

    shouldComponentUpdate() {
        return !this.activeSearch;
    }

    setActiveSearch(value) {
        this.activeSearch = value;
    }

    getColumns() {
        return [
            {
                Header: '',
                Cell: props => (
                    <React.Fragment>
                        {this.linkToEditDynamicProfile(null, props.original)}
                    </React.Fragment>
                ),
                maxWidth: 30,
                resizable: false,
                sortable: false
            }, {
                Header: 'PROFILE NAME',
                Cell: props => this.nameLink(null, props.original),
                accessor: 'name',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control dynamicProfileName-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="Dynamic Profile Name"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);
                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            }, {
                Header: 'PROFILE ID',
                accessor: 'profileIds',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control profileId-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="Profile IDs"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterArrayValue(filter, row);
                    return filterValue.includes(filter.value);
                },
                Cell: props => this.profileIdLink(null, props.original),
            }, {
                Header: 'PROFILE TYPE',
                accessor: 'profileType',
                filterable: true,
                Filter: ({ filter, onChange }) => {
                    const list = this.props.currentUser.scope.includes(INNOVID_DYNAMIC_PROFILE) ? ['No Assigned Type', 'dv360', 'innovid'] : ['No Assigned Type', 'dv360'];
                    const noType = list.indexOf('No Assigned Type');

                    if (noType > 0) {
                        list.splice(noType, 1);
                        list.unshift('No Assigned Type');
                    }

                    return (<Select
                        value={filter && filter.value ? { value: filter.value, label: filter.value } : null}
                        isClearable
                        isSearchable
                        placeholder="Select Profile Type..."
                        onChange={event => onChange(event ? event.value : null)}
                        options={list.map(item => ({ value: item, label: item }))}
                    />);
                },
                filterMethod: (filter, row) => {
                    if (!filter.value) {
                        return true;
                    }

                    if (filter.value === 'No Assigned Type' && !row[filter.id]) {
                        return true;
                    }

                    return filter.value === row[filter.id];
                },
                Cell: props => <CellDiv value={props.value} type="text" style={{ textAlign: 'center' }} />,
                maxWidth: 200
            }, {
                Header: 'BRAND',
                accessor: 'Brand.name',
                filterable: true,
                maxWidth: 300,
                Filter: ({ filter, onChange }) => {
                    const list = Object.keys(this.state.optionsByBrand);
                    const noBrand = list.indexOf('No Assigned Brand');
                    if (noBrand > 0) {
                        list.splice(noBrand, 1);
                        list.unshift('No Assigned Brand');
                    }

                    return (<Select
                        value={filter && filter.value ? { value: filter.value, label: filter.value } : null}
                        isClearable
                        isSearchable
                        placeholder="Select BRAND..."
                        onChange={event => onChange(event ? event.value : null)}
                        options={list.map(item => ({ value: item, label: item }))}
                    />);
                },
                filterMethod: (filter, row) => {
                    if (!filter.value) {
                        return true;
                    }

                    if (filter.value === 'No Assigned Brand' && !row[filter.id]) {
                        return true;
                    }

                    return filter.value === row[filter.id];
                }
            }, {
                Header: 'MODIFIED',
                accessor: 'modifiedAt',
                Cell: props => <CellDiv value={this.dateFormatter(props.value)} type="date" style={{ textAlign: 'center' }} />,
                maxWidth: 150
            }, {
                Header: 'CREATED',
                accessor: 'createdAt',
                Cell: props => <CellDiv value={this.dateFormatter(props.value)} type="date" style={{ textAlign: 'center' }} />,
                maxWidth: 150
            }, {
                Header: 'ACTIVE',
                accessor: 'isActive',
                Cell: props => <CellDiv value={<ActiveStatusLabel isActive={props.value}/>} style={{ textAlign: 'center' }} />,
                maxWidth: 100
            }
        ];
    }

    componentDidMount() {
        this.props.fetchList();
    }

    static getDerivedStateFromProps(props, state) {
        const propList = get(props, 'dynamicProfiles.list', []);
        const propUrl = get(props, 'parsedUrl', {});
        const mergeList = [];
        const listInfo = DynamicProfilesTableContainer.createBrandOptions(propList);

        if (propList !== state.list) {
            let showPagination = state.showPagination;

            if (!state.showPagination && propList.length > DEFAULT_PAGE_SIZE) {
                showPagination = true;
            }

            mergeList.push({
                list: propList,
                showPagination,
                ...listInfo
            });
        }

        if (state.currentUrl !== propUrl.href) {
            mergeList.push({
                currentUrl: propUrl.href,
                ...DynamicProfilesTableContainer.convertUriToState(propUrl)
            });
        }

        if (mergeList.length) {
            return Object.assign.apply(null, [
                {},
                state
            ].concat(mergeList));
        }
        return null;
    }

    getdynamicProfileTableInstance() {
        if (!this.dynamicProfileTableInstance) {
            console.warn('React Table - No instance'); // eslint-disable-line
        }

        return this.dynamicProfileTableInstance;
    }

    onFilteredChangeCustom(value, accessor) {
        let filtered = this.state.filtered;

        const tempFilters = filtered.reduce((prev, filter) => {
            prev[filter.id] = filter.value;
            return prev;
        }, {});

        tempFilters[accessor] = value;

        filtered = Object.keys(tempFilters).map((key) => {
            if (tempFilters[key] === null) {
                return false;
            }
            return { id: key, value: tempFilters[key] };
        }).filter(item => item);

        const instance = this.getdynamicProfileTableInstance().current;
        let page = this.state.page;
        if (get(instance, 'state.sortedData', []).length < this.state.page * this.state.pageSize) {
            page = DEFAULT_PAGE;
        }

        this.setState({ filtered, page }, () => this.mirrorStateToUri());
    }

    static createBrandOptions(list) {
        const optionsByBrand = {};
        for (let i = 0; i < list.length; i += 1) {
            const row = list[i];
            const brandName = get(row, 'Brand.name', 'No Assigned Brand');

            if (!optionsByBrand[brandName]) {
                optionsByBrand[brandName] = [];
            }
        }

        return {
            optionsByBrand,
        };
    }

    dateFormatter(cell) {
        return `${moment(cell).format('MM-DD-YYYY')}`;
    }

    linkToEditDynamicProfile(cell, row) {
        return (
            <Link key={0} title="Edit Dynamic Profile" to={`/admin/dynamic-profiles/${row.id}/edit`}>
                <span className="icon-edit" />
            </Link>
        );
    }

    nameLink(cell, row) {
        return (
            <Link title="View Dynamic Profile" to={`/admin/dynamic-profiles/${row.id}`}>
                {row.name}
            </Link>
        );
    }

    profileIdLink(cell, row) {
        return (
            <Link title="View Dynamic Profile" to={`/admin/dynamic-profiles/${row.id}`}>
                {row.profileIds.join(', ')}
            </Link>
        );
    }

    mirrorStateToUri() {
        const query = {};

        this.state.filtered.forEach((filter) => {
            switch (filter.id) {
            case 'name':
                if (filter.value) {
                    query.capabilityGroupName = filter.value;
                }
                break;

            default:
                // ignore
            }
        });

        if (this.state.page !== DEFAULT_PAGE) {
            query.page = this.state.page;
        }

        if (this.state.pageSize !== DEFAULT_PAGE_SIZE) {
            query.pageSize = this.state.pageSize;
        }

        if (this.state.sorted.length) {
            const sortedItem = this.state.sorted[0];

            query.sort = sortedItem.id;
            query.sortDir = sortedItem.desc ? 'desc' : 'asc';
        }

        this.props.parsedUrl.set('query', query);
        this.props.push(this.props.parsedUrl.toString({ onlyPath: true }));
    }

    static convertUriToState(url) {
        const filtered = [];
        const sorted = [];
        const { query } = url;

        if (query.capabilityGroupName) {
            filtered.push({ id: 'name', value: query.capabilityGroupName });
        }

        if (query.sort) {
            sorted.push({ id: query.sort, desc: query.sortDir === 'desc' });
        }

        let page = Number(query.page);
        if (!page || page < 0) {
            page = DEFAULT_PAGE;
        }

        let pageSize = Number(query.pageSize);
        if (!pageSize || pageSize < 1) {
            pageSize = DEFAULT_PAGE_SIZE;
        }

        return {
            filtered,
            sorted,
            page,
            pageSize
        };
    }

    render() {
        const data = this.props.dynamicProfiles.list;
        const dataFetched = this.props.dynamicProfiles.listFetched;

        return (
            <Grid fluid>
                <ButtonStandardPrimary
                    type="submit"
                    onClick={() => this.props.push('/admin/dynamic-profiles/create')}>
                        Create Dynamic Creative Profile
                </ButtonStandardPrimary>
                <br />
                <br />
                {dataFetched ? (
                    <StyledReactTable
                        data={data}
                        ref={this.dynamicProfileTableInstance}
                        defaultPageSize={25}
                        minRows={10}
                        filtered={this.state.filtered}
                        sorted={this.state.sorted}
                        page={this.state.page}
                        pageSize={this.state.pageSize}
                        onFilteredChange={(filtered, column, value) => {
                            this.onFilteredChangeCustom(value, column.id || column.accessor);
                        }}
                        onSortedChange={sorted => this.setState({ sorted }, () => this.mirrorStateToUri())}
                        onPageChange={page => this.setState({ page }, () => this.mirrorStateToUri())}
                        onPageSizeChange={(pageSize, page) => this.setState({
                            page, pageSize
                        }, () => this.mirrorStateToUri())}
                        getTheadThProps={() => ({
                            style: {
                                backgroundColor: '#E2E2E2',
                            }
                        })}
                        getTheadFilterThProps={() => ({
                            style: {
                                overflow: 'inherit' // allows react-select to work
                            }
                        })}
                        columns={this.getColumns()}
                        defaultSorted={[
                            {
                                id: 'name',
                                desc: true
                            }
                        ]}
                        className="-highlight"
                        showPagination={this.state.showPagination}
                        PaginationComponent={Pagination}
                    />
                ) : (
                    <LoadingTile />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    dynamicProfiles: state.dynamicProfiles,
    parsedUrl: state.router.parsedUrl
});

export default connect(mapStateToProps, { fetchList, push })(DynamicProfilesTableContainer);



// WEBPACK FOOTER //
// ./src/containers/tables/DynamicProfilesTableContainer.js